import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { centralAPI } from '../../../services/central-api';
import ProfilePicture from '../../../components/user/ProfilePicture';
import { NavigationProp, useFocusEffect, useNavigation } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import BasicModal from '../../../components/BasicModal';


interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    userId: number;
    userName: string;
}

export default function UserPhotoModal({ userId, userName, isModalVisible, setModalVisible }: ModalProps) {
    const navigation = useNavigation<NavigationProp<AccessParamList>>();

    const [profilePicture, setProfilePicture] = useState<string>('');

    const closeModal = () => {
        setProfilePicture('');
        setModalVisible(false);
    };

    useFocusEffect(useCallback(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]));


    async function blobUrlToFile(blobUrl: string, filename: string): Promise<File> {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        return new File([blob], filename, { type: blob.type });
    }

    async function loadPageInfo() {
        try {
            const resp = await centralAPI.getUserPicture({ userId: userId });
            if (resp.facePictureImageUrl !== null) {
                setProfilePicture(resp.facePictureImageUrl);
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    async function formSubmit() {
        try {
            if (profilePicture) {
                await centralAPI.updateUserPicture({
                    userId: userId,
                    photo: await blobUrlToFile(profilePicture, 'profilePicture.jpeg'),
                });
            }

            closeModal();
            navigation.navigate('Users', {
                page: 1,
                limit: 25,
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            title={`${translate('name')}: ${userName}`}
            width='small'
            primaryButton={{
                text: translate('save'),
                onClick: formSubmit,
                disabled: !profilePicture,
            }}
        >
            <ProfilePicture photo={profilePicture} setProfilePicture={setProfilePicture} />
        </BasicModal>
    );
}
